import { useEffect, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/common';
import { FileDropZone } from '@/components/common';
import { Search } from '@/components/common/Search';
import { Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { queryClient } from '@/configuration/react-query';
import {
  STORAGE_LIMIT_EXCEEDED_ERROR_CODE,
  TIME_LIMIT_EXCEEDED_ERROR_CODE,
  UPLOAD_PICKER_TYPES
} from '@/constants';
import { AssetScope } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload } from '@/hooks';
import { useUploadFilePicker } from '@/hooks/useFilePicker';
import { MusicIcon, MusicSquareNewIcon, PauseIcon, PlayIcon, PlusIcon } from '@/resources';
import {
  useDownloadSongMutation,
  usePreviewMusicQuery,
  useSearchMusicQuery
} from '@/services/music.service';
import { DownloadSong } from '@/types';

type FolderMusicModalProps = {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  organizationId?: number | null;
  tag: string;
  onSave: () => void;
  organizationPermissions: string[];
};

type PickedSong = { source: 'provider'; song: DownloadSong } | { source: 'file'; file: File };

const formatDuration = (seconds: number) =>
  `${Math.trunc(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export const FolderMusicModal = ({
  isOpen,
  onClose,
  eventId,
  organizationId,
  tag,
  onSave,
  organizationPermissions
}: FolderMusicModalProps) => {
  const [t] = useTranslation('common');

  const [pickedSong, setPickedSong] = useState<PickedSong | null>(null);
  const [fileUrl, setFileUrl] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [showMinLengthMessage, setShowMinLengthMessage] = useState(false);

  const hasMusicProviderAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.MUSIC_PROVIDER
  );

  const searchMusicQuery = useSearchMusicQuery(String(search), hasMusicProviderAccess);
  const music = hasMusicProviderAccess ? searchMusicQuery.data : null;
  const isLoading = hasMusicProviderAccess ? searchMusicQuery.isLoading : false;
  const isShowingResults = hasMusicProviderAccess && search.length >= 2;

  const pickedProviderSongId = pickedSong?.source === 'provider' ? pickedSong.song.id : undefined;

  const { mutateAsync: downloadSong } = useDownloadSongMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('downloadSongSuccess'));
    },
    onError: (error) => {
      const backendError = error.response?.data?.ErrorMessages?.[0];
      const message = backendError?.DisplayMessage ?? t('downloadSongError');
      const errorCode = backendError?.Code;
      const isLimitExceeded =
        !!errorCode &&
        [STORAGE_LIMIT_EXCEEDED_ERROR_CODE, TIME_LIMIT_EXCEEDED_ERROR_CODE].includes(errorCode);

      if (isLimitExceeded) {
        showToast(ToastType.WARNING, {
          text: message,
          autoClose: false
        });
        return;
      }

      showToast(ToastType.ERROR, message);
    }
  });

  const { data: previewMusic, refetch: refetchPreview } = usePreviewMusicQuery(
    Number(pickedProviderSongId)
  );

  useEffect(() => {
    if (pickedProviderSongId !== undefined) {
      refetchPreview();
    }
  }, [pickedProviderSongId]);

  useEffect(() => {
    if (pickedSong?.source !== 'file') {
      setFileUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(pickedSong.file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pickedSong]);

  useEffect(() => {
    if (search.length > 0 && search.length < 2) {
      const timer = setTimeout(() => {
        setShowMinLengthMessage(true);
      }, 2000);

      return () => {
        clearTimeout(timer);
        setShowMinLengthMessage(false);
      };
    }
  }, [search]);

  const { uploadFiles: uploadAudioFiles } = useFileUpload({
    pickerTypes: UPLOAD_PICKER_TYPES.AUDIO,
    eventId,
    organizationId,
    assetScope: AssetScope.PRIVATE,
    assetTag: tag,
    maxFiles: 1
  });

  const pickFile = (files: File[]) => {
    if (files[0]) {
      setPickedSong({ source: 'file', file: files[0] });
    }
  };

  const { openFilePicker, hiddenInputProps } = useUploadFilePicker({
    pickerTypes: UPLOAD_PICKER_TYPES.AUDIO,
    multiple: false,
    disabled: isSaving,
    onFilesSelected: pickFile
  });

  const getPlayer = (): { src?: string; title: string } => {
    if (!pickedSong) {
      return { title: '-' };
    }
    if (pickedSong.source === 'file') {
      return { src: fileUrl, title: pickedSong.file.name };
    }
    return {
      src: previewMusic?.data,
      title: `${pickedSong.song.artist} - ${pickedSong.song.title}`
    };
  };
  const { src: playerSrc, title: playerTitle } = getPlayer();

  const handleSave = async (songToSave: PickedSong | null = pickedSong) => {
    if (!songToSave || isSaving) {
      return;
    }
    setIsSaving(true);

    try {
      if (songToSave.source === 'file') {
        const result = await uploadAudioFiles([songToSave.file]);
        if (result.uploadedCount === 0) {
          setIsSaving(false);
          return;
        }
      } else {
        const { song } = songToSave;
        await downloadSong({
          payload: {
            id: song.id,
            title: song.title,
            artist: song.artist,
            duration: song.duration,
            tag,
            eventId: Number(eventId),
            organizationId: organizationId ?? undefined
          }
        });
      }
      onSave();
      onClose();
    } catch {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      height='h-auto'
      label='addMusic'
      hasButton
      hasCancelButton
      buttonText={t('save')}
      buttonClasses='flex justify-end [&>button]:w-[150px] [&>button]:justify-center'
      handleSubmit={() => handleSave()}
      isButtonDisabled={!pickedSong || isSaving}
      modalClasses='w-full md:w-[640px] border border-secondary-blue-gray'>
      <div className='relative w-full'>
        <div className='mb-4 border-b border-secondary-blue-gray' />
        <p className='mb-4 text-sm text-secondary-gray'>{t('folderMusicDescription')}</p>
        {isSaving && (
          <div className='absolute inset-0 z-50 flex items-center justify-center bg-white'>
            <Spinner />
          </div>
        )}
        <AudioPlayer
          customIcons={{ play: <PlayIcon />, pause: <PauseIcon /> }}
          header={<p className='truncate text-sm font-semibold'>{playerTitle}</p>}
          autoPlay={false}
          src={playerSrc}
          className={`w-full ${!playerSrc && 'opacity-50'}`}
        />

        <div className='mt-4'>
          <Search
            placeholder={t('searchForASong')}
            containerClassName='!border-blue-powder focus-within:!border-primary-blue'
            callback={(query) => setSearch(String(query))}
            disabled={!hasMusicProviderAccess}
          />
          {!hasMusicProviderAccess && (
            <Label
              text={t('yourOranizationDoesntHavePermission', {
                permission: t(`${ORGANIZATION_PERMISSION_TYPE.MUSIC_PROVIDER}`)
              })}
              type={LABEL_TYPE.PARAGRAPH2}
              className='mt-2 !text-secondary-gray'
            />
          )}
          {showMinLengthMessage && (
            <Label
              text={t('nameMin')}
              type={LABEL_TYPE.PARAGRAPH2}
              className='mt-2 !text-accent-red'
            />
          )}
        </div>

        <input {...hiddenInputProps} />
        {isShowingResults ? (
          <div className='mt-2 h-[134px]'>
            {isLoading ? (
              <div className='mt-10'>
                <Spinner />
              </div>
            ) : music?.data.length === 0 ? (
              <Label
                text={t('noResults')}
                type={LABEL_TYPE.PARAGRAPH2}
                className='mt-4 !text-secondary-gray'
              />
            ) : (
              <div className='no-scrollbar flex h-full flex-col overflow-y-auto'>
                {music?.data.map((song: DownloadSong) => {
                  const isPicked = pickedProviderSongId === song.id;
                  return (
                    <div
                      key={song.id}
                      className={`flex w-full flex-row items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm ${
                        isPicked ? 'font-semibold text-primary-blue' : ''
                      }`}>
                      <span className='flex min-w-0 flex-row items-center gap-3'>
                        <MusicIcon
                          className={`size-4 shrink-0 ${isPicked ? 'fill-primary-blue' : 'fill-secondary-gray'}`}
                        />
                        <span className='truncate'>
                          {song.artist} - {song.title}
                        </span>
                      </span>
                      <span className='flex shrink-0 flex-row items-center gap-4'>
                        {!!song.duration && (
                          <span className={isPicked ? '' : 'text-secondary-gray'}>
                            {formatDuration(song.duration)}
                          </span>
                        )}
                        <button
                          type='button'
                          className='flex items-center justify-center'
                          onClick={() => setPickedSong({ source: 'provider', song })}>
                          <PlayIcon
                            className={`hover:text-primary-blue ${
                              isPicked ? 'text-primary-blue' : 'text-secondary-gray'
                            }`}
                          />
                        </button>
                        <button
                          type='button'
                          className='flex items-center justify-center'
                          disabled={isSaving}
                          onClick={() => handleSave({ source: 'provider', song })}>
                          <PlusIcon className='text-secondary-gray hover:text-primary-blue' />
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <FileDropZone
            onDrop={pickFile}
            allowMultipleFiles={false}
            acceptedFormats={['audio/*']}
            disabled={isSaving}
            onClick={openFilePicker}
            className='mt-2 flex h-[134px] w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-[1.5px] border-dashed border-blue-powder'
            activeClassName='bg-color-light-blue ring-4 ring-accent-light-blue'>
            <MusicSquareNewIcon className='mb-3 size-10' />
            {hasMusicProviderAccess && (
              <span className='text-center text-sm text-secondary-gray'>
                {t('findSongInDatabaseOr')}
              </span>
            )}
            <span className='mt-1 text-sm font-semibold text-primary-blue'>{t('uploadSong')}</span>
          </FileDropZone>
        )}
      </div>
    </Modal>
  );
};
